import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import axios from '../utils/api.js';
import theme from '../UserDashboard/theme.js';
import { getClientEmail } from '../utils/getClientEmail';

const accent = theme.colors.brandDark;
const FETCH_LIMIT = 50;

function computeFinalPrice(amount, discount = 0) {
  const a = Number(amount) || 0;
  const d = Number(discount) || 0;
  return Math.round(a - d);
}

function displayFinalPrice(plan) {
  const price = plan?.price;
  if (!price) return null;
  if (price.finalPrice != null && price.finalPrice !== '') return Number(price.finalPrice);
  return computeFinalPrice(price.amount, price.discount);
}

function formatMoney(amount, currency = 'INR') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency || 'INR'} ${amount}`;
  }
}

function formatDateTime(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getEmail() {
  return getClientEmail();
}

/** Add-on purchases allowed only on calendar days 1–25 (API enforces the same). */
function getAddonPurchaseBlockReason() {
  const day = new Date().getDate();
  if (day >= 26) {
    return 'Add-on purchases are only available from the 1st through the 25th of each month.';
  }
  return null;
}

function statusChipColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'inactive') return 'error';
  return 'default';
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Razorpay));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.body.appendChild(script);
  });
}

function PlanDetail({ label, value }) {
  return (
    <Box sx={styles.detailRow}>
      <Typography sx={styles.detailLabel}>{label}</Typography>
      <Typography sx={styles.detailValue}>{value ?? '—'}</Typography>
    </Box>
  );
}

function AddonPlanCard({ plan, onBuyPlan, buying, purchaseBlockedReason }) {
  const currency = plan.price?.currency || 'INR';
  const amount = plan.price?.amount;
  const discount = Number(plan.price?.discount) || 0;
  const finalPrice = displayFinalPrice(plan);
  const status = plan.status || 'draft';
  const isActive = String(status).toLowerCase() === 'active';
  const blocked = Boolean(purchaseBlockedReason);

  return (
    <Paper elevation={0} sx={styles.planCard}>
      <Box sx={styles.planHeader}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <ExtensionOutlinedIcon sx={{ color: accent, fontSize: 22 }} />
            <Typography sx={styles.planName}>{plan.planName}</Typography>
            <Chip
              label={status}
              size="small"
              color={statusChipColor(status)}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 11, height: 22, textTransform: 'capitalize' }}
            />
            {plan.planCategory ? (
              <Chip label={plan.planCategory} size="small" sx={{ fontSize: 11, height: 22 }} />
            ) : null}
          </Box>
          {plan.description ? (
            <Typography sx={styles.planDesc}>{plan.description}</Typography>
          ) : null}
        </Box>
        <Box sx={styles.priceBlock}>
          {discount > 0 && amount != null ? (
            <Typography sx={styles.basePriceStrike}>{formatMoney(amount, currency)}</Typography>
          ) : null}
          <Typography sx={styles.finalPrice}>{formatMoney(finalPrice, currency)}</Typography>
          {discount > 0 ? (
            <Typography sx={styles.discountNote}>
              {formatMoney(discount, currency)} discount
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mt: 2 }}>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail label="Hours / month" value={plan.hoursPerMonth ?? plan.totalHoursPerMonth} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail label="Total hours / month" value={plan.totalHoursPerMonth} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail label="Plan validity (days)" value={plan.planValidity} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail label="Validity (months)" value={plan.validityMonths} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail label="Unlimited" value={plan.isUnlimited ? 'Yes' : 'No'} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail
            label="Auto-pay"
            value={
              plan.autoPayEnabled
                ? `Yes (${plan.autoPayPeriodMonths || 1} mo period)`
                : 'No'
            }
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <PlanDetail label="Free months" value={plan.freeMonths ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <PlanDetail label="Created" value={formatDateTime(plan.createdAt)} />
        </Grid>
      </Grid>

      {blocked ? (
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mt: 2 }}>
          {purchaseBlockedReason}
        </Typography>
      ) : null}

      <Box sx={styles.buyRow}>
        <Button
          variant="contained"
          disabled={!isActive || buying || blocked}
          onClick={() => onBuyPlan(plan)}
          startIcon={buying ? <CircularProgress size={18} color="inherit" /> : <ShoppingCartOutlinedIcon />}
          sx={styles.buyBtn}
        >
          {buying ? 'Opening checkout…' : 'Buy plan'}
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * Lists add-on plans from GET /get-addonplans with Razorpay purchase flow.
 * @param {{ compact?: boolean, robotId?: string, onPaymentSuccess?: () => void }} props
 */
export function AddonPlanView({ compact = false, robotId, onPaymentSuccess }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await axios.get('/get-addonplans', {
        params: { page: 1, limit: FETCH_LIMIT, status: 'active' },
      });
      const rows = res.data?.data;
      const list = Array.isArray(rows) ? rows : [];
      const active = list.filter((p) => String(p.status || '').toLowerCase() === 'active');
      setPlans(active.length ? active : list);
      setTotalCount(res.data?.pagination?.total ?? list.length);
      if (!list.length) {
        setMessage('No add-on plans are available right now.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setPlans([]);
        setTotalCount(0);
        setMessage(err.response?.data?.message || 'No add-on plans found.');
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            'Failed to load add-on plans.',
        );
        setPlans([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const addonPurchaseBlockReason = getAddonPurchaseBlockReason();

  const startPurchase = async (plan) => {
    if (!plan || buyingPlanId) return;
    const email = getEmail();
    if (!email) {
      setSnackbar({
        open: true,
        message: 'Please sign in again to purchase an add-on.',
        severity: 'error',
      });
      return;
    }
    if (addonPurchaseBlockReason) {
      setSnackbar({ open: true, message: addonPurchaseBlockReason, severity: 'warning' });
      return;
    }

    const planId = plan._id;
    setBuyingPlanId(planId);

    try {
      const orderRes = await axios.post('/payment/create-addon-order', {
        addOnPlanId: planId,
      });

      const key = orderRes.data?.key || orderRes.data?.razorpayKeyId;
      const orderId = orderRes.data?.orderId || orderRes.data?.order?.id;
      const amount = orderRes.data?.amount || orderRes.data?.order?.amount;

      if (!key || !orderId) {
        throw new Error('Payment could not be started. Missing Razorpay order details.');
      }

      const Razorpay = await loadRazorpayScript();
      const email = getEmail();

      await new Promise((resolve, reject) => {
        const options = {
          key,
          amount,
          currency: orderRes.data?.currency || plan.price?.currency || 'INR',
          order_id: orderId,
          name: 'Spotless AI',
          description: plan.planName,
          prefill: { email },
          theme: { color: '#2563eb' },
          handler: async (response) => {
            try {
              const purchaseRes = await axios.post('/user/purchase-addon', {
                addOnPlanId: planId,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              });
              const added = purchaseRes.data?.data?.addedHours;
              const baseMsg =
                purchaseRes.data?.message ||
                `Add-on "${plan.planName}" purchased successfully.`;
              setSnackbar({
                open: true,
                message: added != null ? `${baseMsg} (+${added} hours)` : baseMsg,
                severity: 'success',
              });
              onPaymentSuccess?.();
              resolve();
            } catch (purchaseErr) {
              reject(purchaseErr);
            }
          },
          modal: {
            ondismiss: () => reject(new Error('Payment cancelled')),
          },
        };

        const rzp = new Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          const msg =
            resp.error?.description || resp.error?.reason || 'Payment failed. Please try again.';
          reject(new Error(msg));
        });
        rzp.open();
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Could not complete add-on purchase.';
      if (msg !== 'Payment cancelled') {
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setBuyingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: compact ? 200 : 320 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!plans.length) {
    return (
      <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
        {message || 'No add-on plans available.'}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={compact ? undefined : styles.pageWrap}>
        {!compact && <Typography sx={styles.pageTitle}>Add-on plans</Typography>}
        {!compact && (
          <Typography sx={styles.pageSubtitle}>
            Extra hours you can purchase on top of your main subscription. One add-on per calendar
            month; purchases only on days 1–25.
          </Typography>
        )}
        {addonPurchaseBlockReason ? (
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
            {addonPurchaseBlockReason}
          </Alert>
        ) : null}
        {robotId && compact ? (
          <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
            Robot: {robotId} · {plans.length} active plan{plans.length === 1 ? '' : 's'}
            {totalCount > plans.length ? ` (${totalCount} total in catalog)` : ''}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
            {plans.length} active add-on plan{plans.length === 1 ? '' : 's'}
            {totalCount > plans.length ? ` · ${totalCount} total` : ''}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: compact ? 'calc(100vh - 320px)' : undefined,
            overflow: compact ? 'auto' : undefined,
          }}
        >
          {plans.map((plan) => (
            <AddonPlanCard
              key={plan._id}
              plan={plan}
              onBuyPlan={startPurchase}
              buying={buyingPlanId === plan._id}
              purchaseBlockedReason={addonPurchaseBlockReason}
            />
          ))}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2, width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AddonPlanView;

const styles = {
  pageWrap: {
    py: { xs: 1, md: 2 },
  },
  pageTitle: {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: 700,
    color: theme.colors.textPrimary,
    mb: 0.5,
  },
  pageSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    mb: 3,
  },
  planCard: {
    p: { xs: 2, md: 2.5 },
    borderRadius: `${theme.radius.lg}px`,
    border: `1px solid ${theme.colors.borderDefault}`,
    bgcolor: theme.colors.bgPrimary,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  },
  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
    flexWrap: 'wrap',
  },
  planName: {
    fontSize: { xs: '1.1rem', md: '1.25rem' },
    fontWeight: 700,
    color: theme.colors.textPrimary,
  },
  planDesc: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    mt: 0.75,
    lineHeight: 1.5,
  },
  priceBlock: {
    textAlign: 'right',
    flexShrink: 0,
  },
  basePriceStrike: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textDecoration: 'line-through',
  },
  finalPrice: {
    fontSize: { xs: '1.35rem', md: '1.5rem' },
    fontWeight: 800,
    color: accent,
    lineHeight: 1.2,
  },
  discountNote: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.accentSuccess,
    mt: 0.25,
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.textPrimary,
    wordBreak: 'break-word',
  },
  buyRow: {
    mt: 2.5,
    pt: 2,
    borderTop: `1px solid ${theme.colors.borderDefault}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  buyBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
    borderRadius: 2,
    px: 3,
    py: 1.1,
    bgcolor: accent,
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    '&:hover': {
      bgcolor: theme.colors.brandPrimary,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
    },
    '&.Mui-disabled': {
      bgcolor: 'rgba(37, 99, 235, 0.35)',
      color: 'rgba(255,255,255,0.9)',
    },
  },
};
